import React, { useState, useEffect, useRef } from "react";
import { Search, Code, X, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import silaeVariableService from "@/services/silaeVariableService";

import type { SilaeVariable, SilaeVariableCategory } from "@/types/silae";

interface SilaeVariableSearchProps {
  onVariableSelect?: (variable: SilaeVariable) => void;
  placeholder?: string;
  className?: string;
}

export const SilaeVariableSearch: React.FC<SilaeVariableSearchProps> = ({
  onVariableSelect,
  placeholder,
  className = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<SilaeVariable[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isSelectingRef = useRef(false);

  // Debounced search
  useEffect(() => {
    // Skip search if we're selecting a suggestion
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        performSearch(searchTerm.trim());
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll to selected item
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  const performSearch = async (term: string) => {
    try {
      setLoading(true);
      const results =
        await silaeVariableService.searchSilaeVariablesWithPriority(term);
      setSuggestions(results);
      setIsOpen(results.length > 0);
      setSelectedIndex(-1);
      // Reset suggestion refs array
      suggestionRefs.current = [];
    } catch (error) {
      console.error("Error searching SILAE variables:", error);
      toast.error("Erreur lors de la recherche");
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev < suggestions.length - 1 ? prev + 1 : 0; // Loop to first
          return next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : suggestions.length - 1; // Loop to last
          return next;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleVariableSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleVariableSelect = (variable: SilaeVariable) => {
    // Set flag to prevent search when setting search term
    isSelectingRef.current = true;
    setSearchTerm(variable.code);
    setIsOpen(false);
    setSelectedIndex(-1);
    setSuggestions([]); // Clear suggestions immediately
    onVariableSelect?.(variable);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const getCategoryBadgeColor = (category: SilaeVariableCategory) => {
    switch (category) {
      case "bulletin":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300";
      case "salary":
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300";
      case "employee":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "mtpart":
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300";
      case "functions":
        return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder || "Rechercher une variable SILAE..."}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          className="pl-10 pr-10"
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearch}
            className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 hover:bg-muted"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Suggestions Popover */}
      {isOpen && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 shadow-xl border-0 bg-card/95 backdrop-blur-sm">
          <CardContent className="p-0 rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              </div>
            ) : suggestions.length > 0 ? (
              <div className="divide-y divide-border/30">
                {suggestions.map((variable, index) => (
                  <div
                    key={variable.id}
                    ref={(el) => {
                      suggestionRefs.current[index] = el;
                    }}
                    className={`p-4 cursor-pointer transition-all duration-200 ease-in-out group border-b-0 ${
                      index === selectedIndex
                        ? "bg-gradient-to-r from-primary/15 to-primary/5 border-l-4  border-primary shadow-sm"
                        : "hover:bg-gradient-to-r hover:from-muted/80 hover:to-muted/30 hover:shadow-sm"
                    }`}
                    onClick={() => handleVariableSelect(variable)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <Code
                            className={`h-4 w-4 flex-shrink-0 transition-colors ${
                              index === selectedIndex
                                ? "text-primary"
                                : "text-muted-foreground group-hover:text-foreground"
                            }`}
                          />
                          <span
                            className={`font-mono text-sm font-semibold truncate transition-colors ${
                              index === selectedIndex
                                ? "text-primary"
                                : "text-foreground group-hover:text-foreground"
                            }`}
                          >
                            {variable.code}
                          </span>

                          {variable.category && (
                            <Badge
                              variant="outline"
                              className={`text-xs font-medium px-2 py-0.5 ${getCategoryBadgeColor(
                                variable.category,
                              )}`}
                            >
                              {variable.category}
                            </Badge>
                          )}
                        </div>
                        <p
                          className={`text-sm line-clamp-2 leading-relaxed transition-colors ${
                            index === selectedIndex
                              ? "text-foreground/80"
                              : "text-muted-foreground group-hover:text-foreground/70"
                          }`}
                        >
                          {variable.definition}
                        </p>
                      </div>
                      <ExternalLink
                        className={`h-4 w-4 flex-shrink-0 ml-3 transition-colors ${
                          index === selectedIndex
                            ? "text-primary"
                            : "text-muted-foreground/60 group-hover:text-foreground/70"
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchTerm.length >= 2 ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                Aucune variable trouvée pour "{searchTerm}"
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
